import type { PayloadAction } from "@reduxjs/toolkit";
import classNames from "classnames";
import { produce } from "immer";
import React from "react";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import * as financingAPI from "../../../api/financing";
import { ErrorBoundary } from "../../../common/ErrorBoundary";
import { Form } from "../../../forms/Form";
import { FormSubmit } from "../../../forms/FormSubmit";
import { NonFieldErrors } from "../../../forms/NonFieldErrors";
import { strings } from "../../../localization";
import {
    IAddress,
    IAddressCountry,
    IAddressState,
    IBillingAddress,
} from "../../../models/address";
import {
    FinancingAccountInquiry,
    FinancingPreQualRequest,
    FinancingPreQualRequestResponse,
    FinancingPreQualResponse,
    PreQualCustomerResponse,
    PreQualStatus,
} from "../../../models/financing";
import { objectKeys } from "../../../utils/functional";
import { focusElement } from "../../../utils/keyboardFocus";
import {
    checkPreScreenStatus,
    clearPreScreenOffer,
} from "../../financing/reducers";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { AddressSuggestionModal } from "../components/AddressSuggestionModal";
import { AddressWarningModal } from "../components/AddressWarningModal";
import { PRIMARY_PAYMENT_METHOD_KEY, Step } from "../constants";
import { selectSavedAddressFields } from "../helper-utils";
import { BillingAddressForm } from "../payment/BillingAddressForm";
import {
    addPaymentMethod,
    resetPaymentMethodsForm,
    resetPaymentStates,
    setFields,
    setPaymentMethodFields,
    useShippingAddrAsBillingAddr,
} from "../reducers";
import { IReduxFormState } from "../reducers.interfaces";
import { BillingAddressSummary } from "./BillingAddressSummary";
import { CheckoutStep } from "./CheckoutStepContainer";

export interface IPreScreenModalProps {
    isOpen: boolean;
    preApprovalLimit: string;
    preApprovalOfferID: string;
    onRequestClose: (custResp: PreQualCustomerResponse) => void;
    onNewAccount: (account: FinancingAccountInquiry) => void;
}

const enum ModalState {
    CLOSED,
    PRESCREEN_OPEN,
}

interface IComponentErrors {
    non_field_errors?: string[];
    billing_first_name?: string[];
    billing_last_name?: string[];
    billing_line1?: string[];
    billing_line2?: string[];
    billing_line4?: string[];
    billing_state?: string[];
    billing_postcode?: string[];
    billing_country?: string[];
    billing_phone_number?: string[];
}

const addressesAreSame = (addr: IAddress, form: IReduxFormState): boolean => {
    const line1 = addr.line1 === form.billing_line1;
    const line2 = addr.line2 === form.billing_line2;
    const line4 = addr.line4 === form.billing_line4;
    const state = addr.state === form.billing_state;
    const postcode = addr.postcode === form.billing_postcode;
    return line1 && line2 && line4 && state && postcode;
};

const getErrorCount = (errors: IComponentErrors): number => {
    return objectKeys(errors).reduce((memo, field) => {
        const fieldErrors = errors[field];
        const numErrors = fieldErrors ? fieldErrors.length : 0;
        return memo + numErrors;
    }, 0);
};

const mapPhoneNumberError = (errors: { [s: string]: string }): void => {
    if (errors.phone_number) {
        errors.billing_phone_number = errors.phone_number;
        delete errors.phone_number;
    }
};

export const BillingAddress = (props: {
    heading: string;
    errors: string[];
    showFinancingPreQual?: boolean;
    isCurrentStep: boolean;
    buildPreScreenModal?: (
        props: IPreScreenModalProps,
    ) => React.JSX.Element | null;
    saveBillingAddress: (bypassUSPSValidation: boolean) => Promise<IAddress>;
    onContinue: (event?: React.MouseEvent<HTMLElement>) => void;
    onEdit: (event?: React.MouseEvent<HTMLElement>) => void;
    formRef?: React.RefObject<HTMLFormElement | null>;
}): React.ReactNode => {
    const user = useAppSelector((rootState) => rootState.common.user);
    const form = useAppSelector((rootState) => rootState.checkout.form);
    const countries = useAppSelector(
        (rootState) => rootState.checkout.data.countries,
    );
    const states = useAppSelector(
        (rootState) => rootState.checkout.data.billing_states,
    );
    const savedAddresses = useAppSelector(
        (rootState) => rootState.checkout.data.addresses,
    );
    const billingMethodErrors = useAppSelector(
        (rootState) => rootState.checkout.data.billing_method_errors,
    );
    const paymentMethods = useAppSelector(
        (rootState) => rootState.checkout.data.payment_methods,
    );
    const showForm = useAppSelector(
        (rootState) =>
            rootState.checkout.form.current_step === Step.BILLING_ADDRESS,
    );

    const dispatch = useAppDispatch();

    const [modalState, setModalState] = React.useState<ModalState>(
        ModalState.CLOSED,
    );
    const [isSaving, setIsSaving] = React.useState(false);
    const [showErrorMessages, setShowErrorMessages] = React.useState(false);
    const [preApprovalLimit, setPreApprovalLimit] = React.useState<
        string | null
    >(null);
    const [preApprovalOfferID, setPreApprovalOfferID] = React.useState<
        string | null
    >(null);
    const [showAddressWarningModal, setShowAddressWarningModal] =
        React.useState(false);
    const [showAddressSuggestionModal, setShowAddressSuggestionModal] =
        React.useState(false);
    const [suggestedAddress, setSuggestedAddress] =
        React.useState<IAddress | null>(null);
    const [errors, setErrors] = React.useState<IComponentErrors>({});

    const submitButtonRef = React.useRef<HTMLInputElement>(null);
    const prevModalStateRef = React.useRef<ModalState>(modalState);

    // Track pre-screen modal analytics beacon
    React.useEffect(() => {
        const prescreenWasOpen =
            prevModalStateRef.current === ModalState.PRESCREEN_OPEN;
        const prescreenIsOpen = modalState === ModalState.PRESCREEN_OPEN;

        if (preApprovalOfferID && prescreenIsOpen && !prescreenWasOpen) {
            void financingAPI.sendPreScreenOfferDisplayConfirmation(
                preApprovalOfferID,
            );
        }

        prevModalStateRef.current = modalState;
    }, [modalState, preApprovalOfferID]);

    const continueToNext = () => {
        setIsSaving(false);
        props.onContinue();
    };

    const openPreScreenModal = (preQualResp: FinancingPreQualResponse) => {
        const document = getDocument();
        if (document) {
            document.body.setAttribute("style", "overflow-y: hidden");
        }
        setPreApprovalLimit(preQualResp.credit_limit);
        setPreApprovalOfferID(preQualResp.response_id);
        setModalState(ModalState.PRESCREEN_OPEN);
    };

    const closeModal = () => {
        const document = getDocument();
        if (document) {
            document.body.setAttribute("style", "overflow: initial");
        }
        setModalState(ModalState.CLOSED);
    };

    const abortSave = () => {
        setIsSaving(false);
        setShowErrorMessages(true);
    };

    const showSuggestionModal = (addr: IAddress) => {
        setShowAddressSuggestionModal(true);
        setSuggestedAddress(addr);
    };

    const showWarningModal = () => {
        setShowAddressWarningModal(true);
    };

    const displayInlineErrors = (responseErrors: { [s: string]: string }) => {
        mapPhoneNumberError(responseErrors);
        setIsSaving(false);
        setErrors(responseErrors);
        setShowErrorMessages(true);
    };

    const checkPreQualificationStatus = async () => {
        if (user && user.is_csr) {
            continueToNext();
            return;
        }

        if (!props.showFinancingPreQual) {
            continueToNext();
            return;
        }

        const prequalFormData: FinancingPreQualRequest = {
            first_name: form.billing_first_name,
            last_name: form.billing_last_name,
            line1: form.billing_line1,
            city: form.billing_line4,
            state: form.billing_state,
            postcode: form.billing_postcode.slice(0, 5),
            email: form.guest_email,
            phone: form.billing_phone_number,
        };

        let prescreenReqResp: PayloadAction<
            FinancingPreQualRequestResponse | undefined
        >;
        try {
            prescreenReqResp = await dispatch(
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                checkPreScreenStatus(prequalFormData) as any,
            );
        } catch (err) {
            const serverErrors = err.response.body || {
                detail: "An unknown error occurred",
            };
            console.error(serverErrors);
            continueToNext();
            return;
        }

        const approvedResponse = prescreenReqResp?.payload?.responses.find(
            (resp) => resp.status === PreQualStatus.APPROVED,
        );
        if (!approvedResponse) {
            continueToNext();
            return;
        }

        if (props.buildPreScreenModal) {
            openPreScreenModal(approvedResponse);
        }
    };

    const saveAddress = async (bypassUSPSValidation: boolean) => {
        if (billingMethodErrors.length > 0) {
            return;
        }

        setIsSaving(true);
        setErrors({});
        setShowAddressWarningModal(false);
        setShowAddressSuggestionModal(false);

        let addr: IAddress;
        try {
            addr = await props.saveBillingAddress(bypassUSPSValidation);
        } catch (err) {
            if (billingMethodErrors.length > 0) {
                abortSave();
                return;
            }
            if (Object.keys(err.response.body).length === 1) {
                if (err.response.body.billing_state) {
                    displayInlineErrors(err.response.body);
                    return;
                } else if (err.response.body.non_field_errors) {
                    showWarningModal();
                    return;
                }
            }
            displayInlineErrors(err.response.body);
            return;
        }

        if (billingMethodErrors.length > 0) {
            abortSave();
            return;
        }

        if (bypassUSPSValidation || addressesAreSame(addr, form)) {
            void checkPreQualificationStatus();
            return;
        }

        showSuggestionModal(addr);
    };

    const onSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();
        if (getErrorCount(errors) > 0) {
            setShowErrorMessages(true);
            return;
        }
        setIsSaving(true);
        setErrors({});
        return saveAddress(false);
    };

    const onValidStateChange = (fieldName: string, errorMessages: string[]) => {
        setErrors((oldErrs) =>
            produce(oldErrs, (draft) => {
                draft[fieldName as keyof IComponentErrors] = errorMessages;
            }),
        );
    };

    const onBillingAddressChange = (fields: Partial<IBillingAddress>) => {
        dispatch(setFields(fields));
    };

    const setBillingAddressIsSameAsShipping = (isSame: boolean) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        dispatch(useShippingAddrAsBillingAddr(isSame));
    };

    const selectSavedAddress = (
        prefix: "shipping" | "billing",
        savedAddresses: IAddress[],
        selectedAddressURL: string,
    ) => {
        dispatch(
            setFields(
                selectSavedAddressFields(
                    prefix,
                    savedAddresses,
                    selectedAddressURL,
                ),
            ),
        );
    };

    const onPreApprovalError = () => {
        if (modalState !== ModalState.CLOSED) {
            closeModal();
            continueToNext();
        }
    };

    const handleNewAccount = (account: FinancingAccountInquiry) => {
        dispatch(resetPaymentMethodsForm({ methods: paymentMethods }));
        dispatch(resetPaymentStates());
        dispatch(
            addPaymentMethod({
                method_key: PRIMARY_PAYMENT_METHOD_KEY,
                method: {
                    method_type: "financing",
                    amount: null,
                    pay_balance: false,
                    financing_account: account.account_number,
                    new_financing_account: account,
                    financing_plan: null,
                    has_agreed: false,
                    has_esigned: false,
                },
            }),
        );
        dispatch(
            setPaymentMethodFields({
                method_key: PRIMARY_PAYMENT_METHOD_KEY,
                fields: {
                    new_financing_account: account,
                    financing_account: account.account_number,
                },
            }),
        );
        closeModal();
        continueToNext();
    };

    const buildAddressSuggestionModal = () => {
        const onContinue = (shouldImport: boolean) => {
            if (shouldImport && suggestedAddress) {
                dispatch(
                    setFields(
                        selectSavedAddressFields(
                            "billing",
                            [suggestedAddress],
                            suggestedAddress.url,
                        ),
                    ),
                );
            }
            void saveAddress(true);
        };

        const closeSuggestionModal = () => {
            setShowAddressSuggestionModal(false);
            setIsSaving(false);
        };

        const focusOnSubmitButton = () => {
            if (submitButtonRef.current) {
                focusElement(submitButtonRef.current);
            }
        };

        return (
            <AddressSuggestionModal
                form={form}
                suggestedAddress={suggestedAddress}
                onContinue={onContinue}
                onRequestClose={closeSuggestionModal}
                onAfterClose={focusOnSubmitButton}
                showAddressSuggestionModal={showAddressSuggestionModal}
                addressPrefix={"billing"}
            />
        );
    };

    const buildAddressWarningModal = () => {
        const onContinue = () => {
            void saveAddress(true);
        };

        const onEdit = () => {
            setIsSaving(false);
            setShowAddressWarningModal(false);
        };

        return (
            <AddressWarningModal
                form={form}
                onContinue={onContinue}
                onEdit={onEdit}
                showAddressWarningModal={showAddressWarningModal}
                addressPrefix={"billing"}
            />
        );
    };

    const bodyClasses = classNames({
        "checkout-step__body": true,
        "checkout-step__body--collapsed": !showForm,
    });

    let preScreenModal: React.JSX.Element | null = null;
    if (props.showFinancingPreQual && props.buildPreScreenModal) {
        preScreenModal = props.buildPreScreenModal({
            isOpen: modalState === ModalState.PRESCREEN_OPEN,
            preApprovalLimit: preApprovalLimit || "",
            preApprovalOfferID: preApprovalOfferID || "",
            onRequestClose: (custResp) => {
                const prequalresponse =
                    financingAPI.setPreQualCustomerResponse(custResp);
                void prequalresponse.then((response) => {
                    void financingAPI.updatePreScreenChoiceValue(
                        custResp,
                        response.response_id,
                    );
                });

                if (custResp !== PreQualCustomerResponse.ACCEPT) {
                    void dispatch(
                        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
                        clearPreScreenOffer() as any,
                    );
                }
                closeModal();
                continueToNext();
            },
            onNewAccount: (a) => {
                handleNewAccount(a);
            },
        });
    }

    return (
        <CheckoutStep
            className="checkout-step--billing-address form"
            heading={props.heading}
            isCurrentStep={props.isCurrentStep}
        >
            <div className={bodyClasses}>
                <Form onSubmit={onSubmit} ref={props.formRef}>
                    <NonFieldErrors
                        errors={errors.non_field_errors}
                        showErrorMessages={true}
                    />
                    <NonFieldErrors
                        errors={props.errors.concat(billingMethodErrors)}
                        showErrorMessages={true}
                    />
                    <BillingAddressForm
                        countries={countries as readonly IAddressCountry[]}
                        states={states as readonly IAddressState[]}
                        savedAddresses={savedAddresses as readonly IAddress[]}
                        form={form}
                        errors={errors}
                        setBillingAddressIsSameAsShipping={
                            setBillingAddressIsSameAsShipping
                        }
                        selectSavedAddress={selectSavedAddress}
                        onChange={onBillingAddressChange}
                        onValidStateChange={onValidStateChange}
                        showErrorMessages={showErrorMessages}
                        disabled={isSaving}
                    />
                    {form.billing_addr_is_shipping_addr && (
                        <div className="checkout-step__summary-address">
                            {form.shipping_first_name} {form.shipping_last_name}
                            <br />
                            {form.shipping_line1}
                            <br />
                            {form.shipping_line2 ? (
                                <div>{form.shipping_line2}</div>
                            ) : null}
                            {form.shipping_line4}, {form.shipping_state}{" "}
                            {form.shipping_postcode}
                            <br />
                            {form.shipping_phone_number}
                            <br />
                        </div>
                    )}
                    <FormSubmit
                        className="button button--full-width button--continue-to-payment al-checkout__continue-button--to-payment"
                        inputRef={submitButtonRef}
                        value={
                            strings.get(
                                "CONTINUE_BTN_LABEL_BILLING_ADDRESS",
                                // TODO(i18n): Use t tagged template for fallback: ) || t`Continue`
                            ) || "Continue"
                        }
                        disabled={isSaving}
                    />
                </Form>
            </div>
            <BillingAddressSummary onEdit={props.onEdit} />
            {buildAddressWarningModal()}
            {buildAddressSuggestionModal()}
            <ErrorBoundary onError={onPreApprovalError}>
                {preScreenModal}
            </ErrorBoundary>
        </CheckoutStep>
    );
};
